import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BarChart3, TrendingUp, Package, PieChart, FileSpreadsheet, Download, Calendar, FileText, Truck, Store, AlertCircle, CheckCircle2, DollarSign, Layers } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RPieChart, Pie, Cell, Legend } from 'recharts';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatPrice = (p) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(p);
const COLORS = ['#a0d5ba', '#46371c', '#82ba9e', '#75664d', '#bce3cd', '#e5dfd3'];

const statusLabels = { pending: 'Pendiente', approved: 'Aprobado', production: 'Produccion', delivery: 'Entrega', delivered: 'Entregado', cancelled: 'Cancelado' };

export default function AdminReports() {
  const [searchParams] = useSearchParams();
  const [salesData, setSalesData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [statusDist, setStatusDist] = useState([]);
  const [categorySales, setCategorySales] = useState([]);
  const [period, setPeriod] = useState('daily');
  const [loading, setLoading] = useState(true);

  // Advanced Reports States
  const [activeTab, setActiveTab] = useState(() => searchParams.get('type') ? 'advanced' : 'sales');
  const [stores, setStores] = useState([]);
  const [reportType, setReportType] = useState(() => searchParams.get('type') || 'sales_by_ref');
  const [selectedStoreId, setSelectedStoreId] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [deliveryTypeFilter, setDeliveryTypeFilter] = useState('all'); // 'all' | 'delivery' | 'pickup'
  const [startDate, setStartDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [reportData, setReportData] = useState([]);
  const [generatingReport, setGeneratingReport] = useState(false);

  useEffect(() => {
    const typeParam = searchParams.get('type');
    if (typeParam) {
      setReportType(typeParam);
      setActiveTab('advanced');
    }
  }, [searchParams]);

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const fetchStores = async () => {
      try {
        let storesData = [];
        try {
          const adminRes = await axios.get(`${API}/admin/stores`, { headers: getHeaders(), withCredentials: true });
          storesData = adminRes.data;
        } catch {
          const publicRes = await axios.get(`${API}/stores?include_hidden=true`);
          storesData = publicRes.data;
        }
        setStores(storesData);
      } catch (err) {
        console.error('Error fetching stores:', err);
      }
    };
    fetchStores();
  }, []);

  const generateReport = async () => {
    setGeneratingReport(true);
    try {
      let endpoint = '';
      if (reportType === 'sales_by_ref') {
        endpoint = '/admin/reports/sales-by-reference';
      } else if (reportType === 'production') {
        endpoint = '/admin/reports/production';
      } else if (reportType === 'pending_deliveries') {
        endpoint = '/admin/reports/pending-deliveries';
      } else if (reportType === 'payments') {
        endpoint = '/admin/reports/payments';
      } else if (reportType === 'delivered_orders') {
        endpoint = '/admin/reports/delivered-orders';
      }

      const { data } = await axios.get(`${API}${endpoint}`, {
        withCredentials: true,
        headers: getHeaders(),
        params: {
          start_date: startDate,
          end_date: endDate,
          store_id: selectedStoreId,
          delivery_type: deliveryTypeFilter
        }
      });
      setReportData(data);
      toast.success('Reporte generado con éxito');
    } catch (err) {
      console.error(err);
      toast.error('Error al generar el reporte');
    } finally {
      setGeneratingReport(false);
    }
  };

  const exportCSV = () => {
    if (reportData.length === 0) {
      toast.error('No hay datos para exportar');
      return;
    }

    const storeObj = stores.find(s => s.id === selectedStoreId);
    const storeName = storeObj ? storeObj.name.toUpperCase() : 'TODAS';

    let csvContent = '\uFEFF'; // UTF-8 BOM

    if (reportType === 'sales_by_ref') {
      csvContent += `REPORTE DE VENTAS X REFERENCIA\n`;
      csvContent += `TIENDA ${storeName}: Entregas ${startDate} - ${endDate}\n\n`;
      csvContent += `CODIGO,REFERENCIA,TAMANO,CATEGORIA,NUM,VALOR,ORDENES\n`;
      reportData.forEach(row => {
        csvContent += `"${row.codigo}","${row.referencia}","${row.tamano}","${row.categoria}",${row.num},${row.valor},"${row.ordenes}"\n`;
      });
    } else if (reportType === 'production') {
      csvContent += `REPORTE DE PRODUCCION (Todas) TIENDA ${storeName}\n`;
      csvContent += `Para Producir ${startDate} - ${endDate}\n\n`;
      csvContent += `FECHA,ORDEN,PRODUCTO,CANTIDAD,SABOR,RELLENO,TAMANO,CATEGORIA,PRODUCIR (fecha),ENTREGA (fecha),ENTREGA (hora),ESTADO,SEDE\n`;
      reportData.forEach(row => {
        csvContent += `"${row.fecha}","${row.orden}","${row.producto}",${row.cantidad},"${row.sabor}","${row.relleno}","${row.tamano}","${row.categoria}","${row.producir_fecha}","${row.entrega_fecha}","${row.entrega_hora}","${row.estado}","${row.sede}"\n`;
      });
    } else if (reportType === 'pending_deliveries') {
      csvContent += `REPORTE DE ENTREGAS PENDIENTES\n`;
      csvContent += `${storeName}: ${startDate} - ${endDate}\n\n`;
      csvContent += `FECHA,TIENDA,CATEGORIA,CANTIDAD,VALOR,NUM_ORDENES\n`;
      reportData.forEach(row => {
        csvContent += `"${row.fecha}","${row.tienda}","${row.categoria}",${row.cantidad},${row.valor},${row.num_ordenes}\n`;
      });
    } else if (reportType === 'payments') {
      csvContent += `REPORTE DE PAGOS\n`;
      csvContent += `${storeName}: ${startDate} - ${endDate}\n\n`;
      csvContent += `FECHA_PAGO,ORDEN,CLIENTE,USUARIO,METODO,VALOR_PAGO,TOTAL_ORDEN,SALDO_PENDIENTE,SEDE\n`;
      reportData.forEach(row => {
        csvContent += `"${row.fecha_pago}","${row.orden}","${row.cliente}","${row.usuario_pago || 'N/A'}","${row.metodo}",${row.valor_pago},${row.total_orden},${row.saldo_pendiente ?? 0},"${row.sede}"\n`;
      });
    } else if (reportType === 'delivered_orders') {
      csvContent += `REPORTE DE PEDIDOS ENTREGADOS (TIENDA Y DOMICILIO)\n`;
      csvContent += `Sede: ${storeName} | Fechas: ${startDate} al ${endDate} | Filtro: ${deliveryTypeFilter === 'delivery' ? 'Solo Domicilios' : deliveryTypeFilter === 'pickup' ? 'Solo Retiro en Tienda' : 'Todos'}\n\n`;
      csvContent += `ORDEN,FECHA_ENTREGA,HORARIO,TIPO,SEDE,DOMICILIARIO,CLIENTE,TELEFONO,DIRECCION,PRODUCTOS,ITEMS_CANT,TOTAL_ORDEN,VALOR_DOMICILIO,PAGADO,SALDO_PENDIENTE,METODO_PAGO,ESTADO_PAGO\n`;
      let totVentas = 0;
      let totFletes = 0;
      let totPagado = 0;
      let totSaldo = 0;
      let totCantItems = 0;

      reportData.forEach(row => {
        totVentas += (Number(row.total_orden) || 0);
        totFletes += (Number(row.valor_domicilio) || 0);
        totPagado += (Number(row.valor_pagado) || 0);
        totSaldo += (Number(row.saldo_pendiente) || 0);
        totCantItems += (Number(row.total_items_count) || 1);
        const prodSummaryEscaped = (row.productos_resumen || '').replace(/"/g, '""');
        csvContent += `"${row.orden}","${row.fecha_entrega}","${row.hora_entrega}","${row.tipo_entrega_label}","${row.sede}","${row.domiciliario}","${row.cliente}","${row.telefono}","${row.direccion}","${prodSummaryEscaped}",${row.total_items_count || 1},${row.total_orden},${row.valor_domicilio},${row.valor_pagado},${row.saldo_pendiente},"${row.metodo_pago}","${row.estado_pago}"\n`;
      });
      csvContent += `\nTOTALES,,,,,,,,,"${totCantItems} items",${totVentas},${totFletes},${totPagado},${totSaldo},,\n`;
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${reportType}_report_${startDate}_to_${endDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportPDF = () => {
    if (reportData.length === 0) {
      toast.error('No hay datos para exportar');
      return;
    }

    const storeObj = stores.find(s => s.id === selectedStoreId);
    const storeName = storeObj ? storeObj.name.toUpperCase() : 'TODAS';
    
    let reportTitle = '';
    let tableHeaders = [];
    let tableRows = [];

    const filteredData = reportData.filter(row => {
      if (reportType === 'production' && selectedCategory !== 'all') {
        const cat = (row.categoria || '').toLowerCase();
        const sel = selectedCategory.toLowerCase();
        if (sel.includes('personalizada') || sel.includes('tortas personalizadas')) {
          return cat.includes('personalizada');
        }
        return cat === sel;
      }
      return true;
    });

    if (reportType === 'sales_by_ref') {
      reportTitle = 'REPORTE DE VENTAS X REFERENCIA';
      tableHeaders = ['Código', 'Referencia', 'Tamaño', 'Categoría', 'Cant.', 'Valor Total', 'Órdenes'];
      tableRows = filteredData.map(row => [
        row.codigo, row.referencia, row.tamano, row.categoria, row.num, formatPrice(row.valor), row.ordenes
      ]);
    } else if (reportType === 'production') {
      reportTitle = 'REPORTE DE PRODUCCION';
      tableHeaders = ['Fecha', 'Orden', 'Producto', 'Cant.', 'Sabor', 'Relleno', 'Tamaño', 'Categoría', 'Entrega (Fecha)', 'Sede'];
      tableRows = filteredData.map(row => [
        row.fecha, row.orden, row.producto, row.cantidad, row.sabor, row.relleno, row.tamano, row.categoria, row.entrega_fecha, row.sede
      ]);
    } else if (reportType === 'pending_deliveries') {
      reportTitle = 'REPORTE DE ENTREGAS PENDIENTES';
      tableHeaders = ['Fecha', 'Tienda', 'Categoría', 'Cantidad', 'Valor', 'Num Órdenes'];
      tableRows = filteredData.map(row => [
        row.fecha, row.tienda, row.categoria, row.cantidad, formatPrice(row.valor), row.num_ordenes
      ]);
    } else if (reportType === 'payments') {
      reportTitle = 'REPORTE DE PAGOS';
      tableHeaders = ['Fecha Pago', 'Orden', 'Cliente', 'Usuario', 'Método', 'Valor Pago', 'Total Orden', 'Saldo Pendiente', 'Sede'];
      tableRows = filteredData.map(row => [
        row.fecha_pago, row.orden, row.cliente, row.usuario_pago || 'N/A', row.metodo, formatPrice(row.valor_pago), formatPrice(row.total_orden), formatPrice(row.saldo_pendiente || 0), row.sede
      ]);
    } else if (reportType === 'delivered_orders') {
      reportTitle = 'REPORTE DE PEDIDOS ENTREGADOS (TIENDA Y DOMICILIO)';
      tableHeaders = ['Orden', 'Fecha', 'Tipo', 'Sede', 'Domiciliario', 'Cliente / Tel', 'Dirección', 'Productos Entregados', 'Total', 'Pagado', 'Saldo Pend.', 'Método'];
      tableRows = filteredData.map(row => [
        row.orden,
        row.fecha_entrega,
        row.tipo_entrega_label,
        row.sede,
        row.domiciliario,
        `${row.cliente} (${row.telefono})`,
        row.direccion,
        row.productos_resumen,
        formatPrice(row.total_orden),
        formatPrice(row.valor_pagado),
        formatPrice(row.saldo_pendiente || 0),
        row.metodo_pago
      ]);
    }

    let summaryHtml = '';
    if (reportType === 'production') {
      const categoryTotals = {};
      let totalQty = 0;
      filteredData.forEach(row => {
        const cat = row.categoria || 'Sin Categoría';
        const qty = Number(row.cantidad) || 1;
        categoryTotals[cat] = (categoryTotals[cat] || 0) + qty;
        totalQty += qty;
      });

      summaryHtml = `
        <div style="margin-top: 20px; border: 1px solid #e5dfd3; border-radius: 12px; padding: 15px; max-width: 400px; background-color: #fcfbf9; font-size: 11px; float: right;">
          <h3 style="margin-top: 0; color: #46371c; border-bottom: 1px solid #e5dfd3; padding-bottom: 5px; font-size: 12px;">Consolidado de Producción por Categoría</h3>
          <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
            ${Object.entries(categoryTotals).map(([cat, total]) => `
              <tr>
                <td style="padding: 4px 0; font-weight: bold;">${cat}:</td>
                <td style="padding: 4px 0; text-align: right;">${total} ${total === 1 ? 'unidad' : 'unidades'}</td>
              </tr>
            `).join('')}
            <tr style="border-top: 1.5px solid #46371c; font-weight: bold;">
              <td style="padding: 6px 0; color: #46371c;">TOTAL DE PRODUCTOS A PRODUCIR:</td>
              <td style="padding: 6px 0; text-align: right; color: #46371c;">${totalQty} unidades</td>
            </tr>
          </table>
        </div>
        <div style="clear: both;"></div>
      `;
    } else if (reportType === 'payments') {
      const methodTotals = {};
      let totalSum = 0;
      filteredData.forEach(row => {
        const method = row.metodo || 'SIN PAGO';
        const amount = Number(row.valor_pago) || 0;
        methodTotals[method] = (methodTotals[method] || 0) + amount;
        totalSum += amount;
      });

      summaryHtml = `
        <div style="margin-top: 20px; border: 1px solid #e5dfd3; border-radius: 12px; padding: 15px; max-width: 400px; background-color: #fcfbf9; font-size: 11px; float: right;">
          <h3 style="margin-top: 0; color: #46371c; border-bottom: 1px solid #e5dfd3; padding-bottom: 5px; font-size: 12px;">Consolidado de Pagos</h3>
          <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
            ${Object.entries(methodTotals).map(([method, total]) => `
              <tr>
                <td style="padding: 4px 0; font-weight: bold; text-transform: uppercase;">${method}:</td>
                <td style="padding: 4px 0; text-align: right;">${formatPrice(total)}</td>
              </tr>
            `).join('')}
            <tr style="border-top: 1.5px solid #46371c; font-weight: bold;">
              <td style="padding: 6px 0; color: #46371c;">TOTAL RECAUDADO:</td>
              <td style="padding: 6px 0; text-align: right; color: #46371c;">${formatPrice(totalSum)}</td>
            </tr>
          </table>
        </div>
        <div style="clear: both;"></div>
      `;
    } else if (reportType === 'delivered_orders') {
      let totalVentas = 0;
      let totalPagado = 0;
      let totalPendiente = 0;
      let totalFletes = 0;
      let countDelivery = 0;
      let countPickup = 0;
      let cakesSmall = 0;
      let cakesMedium = 0;
      let cakesLarge = 0;
      let cakesOther = 0;
      let totalItems = 0;

      filteredData.forEach(row => {
        totalVentas += (Number(row.total_orden) || 0);
        totalPagado += (Number(row.valor_pagado) || 0);
        totalPendiente += (Number(row.saldo_pendiente) || 0);
        totalFletes += (Number(row.valor_domicilio) || 0);
        if (row.tipo_entrega === 'delivery') countDelivery++;
        else countPickup++;

        if (row.cake_counts) {
          cakesSmall += (row.cake_counts.small || 0);
          cakesMedium += (row.cake_counts.medium || 0);
          cakesLarge += (row.cake_counts.large || 0);
          cakesOther += (row.cake_counts.other || 0);
        }
        totalItems += (Number(row.total_items_count) || 1);
      });

      summaryHtml = `
        <div style="margin-top: 25px; display: flex; gap: 20px; justify-content: space-between; font-size: 11px;">
          <div style="flex: 1; border: 1px solid #e5dfd3; border-radius: 10px; padding: 12px; background-color: #fcfbf9;">
            <h4 style="margin: 0 0 8px 0; color: #46371c; border-bottom: 1px solid #e5dfd3; padding-bottom: 4px; font-size: 12px;">💰 Cuadre de Dinero / Pagos</h4>
            <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
              <tr>
                <td style="padding: 3px 0;">Total Pedidos:</td>
                <td style="padding: 3px 0; text-align: right; font-weight: bold;">${filteredData.length} (Domicilio: ${countDelivery} | Tienda: ${countPickup})</td>
              </tr>
              <tr>
                <td style="padding: 3px 0;">Total Facturado:</td>
                <td style="padding: 3px 0; text-align: right; font-weight: bold;">${formatPrice(totalVentas)}</td>
              </tr>
              <tr>
                <td style="padding: 3px 0;">Fletes Domicilio:</td>
                <td style="padding: 3px 0; text-align: right; font-weight: bold;">${formatPrice(totalFletes)}</td>
              </tr>
              <tr>
                <td style="padding: 3px 0; color: #166534;">Total Recaudado / Pagado:</td>
                <td style="padding: 3px 0; text-align: right; font-weight: bold; color: #166534;">${formatPrice(totalPagado)}</td>
              </tr>
              <tr style="border-top: 1.5px solid #46371c; font-weight: bold;">
                <td style="padding: 6px 0; color: ${totalPendiente > 0 ? '#b45309' : '#166534'};">SALDO PENDIENTE POR COBRAR:</td>
                <td style="padding: 6px 0; text-align: right; color: ${totalPendiente > 0 ? '#b45309' : '#166534'}; font-size: 12px;">${formatPrice(totalPendiente)}</td>
              </tr>
            </table>
          </div>

          <div style="flex: 1; border: 1px solid #e5dfd3; border-radius: 10px; padding: 12px; background-color: #fcfbf9;">
            <h4 style="margin: 0 0 8px 0; color: #46371c; border-bottom: 1px solid #e5dfd3; padding-bottom: 4px; font-size: 12px;">🎂 Control de Inventario Entregado</h4>
            <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
              <tr>
                <td style="padding: 3px 0;">Tortas Pequeñas (Personal / 6-8):</td>
                <td style="padding: 3px 0; text-align: right; font-weight: bold;">${cakesSmall} und</td>
              </tr>
              <tr>
                <td style="padding: 3px 0;">Tortas Medianas (12-15):</td>
                <td style="padding: 3px 0; text-align: right; font-weight: bold;">${cakesMedium} und</td>
              </tr>
              <tr>
                <td style="padding: 3px 0;">Tortas Grandes (20-35):</td>
                <td style="padding: 3px 0; text-align: right; font-weight: bold;">${cakesLarge} und</td>
              </tr>
              <tr>
                <td style="padding: 3px 0;">Otros Productos / Complementos:</td>
                <td style="padding: 3px 0; text-align: right; font-weight: bold;">${cakesOther} und</td>
              </tr>
              <tr style="border-top: 1.5px solid #46371c; font-weight: bold;">
                <td style="padding: 6px 0; color: #46371c;">TOTAL ITEMS ENTREGADOS:</td>
                <td style="padding: 6px 0; text-align: right; color: #46371c; font-size: 12px;">${totalItems} und</td>
              </tr>
            </table>
          </div>
        </div>

        <div style="margin-top: 45px; display: flex; justify-content: space-around; text-align: center; font-size: 11px;">
          <div style="width: 250px; border-top: 1px solid #46371c; padding-top: 6px;">
            <p style="margin: 0; font-weight: bold;">Firma Encargado Sede / Administrador</p>
            <p style="margin: 2px 0 0 0; color: #666; font-size: 9px;">Verificado y Cuadrado</p>
          </div>
          <div style="width: 250px; border-top: 1px solid #46371c; padding-top: 6px;">
            <p style="margin: 0; font-weight: bold;">Firma Domiciliario / Conductor</p>
            <p style="margin: 2px 0 0 0; color: #666; font-size: 9px;">Entregado a Satisfacción</p>
          </div>
        </div>
      `;
    }

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>${reportTitle}</title>
          <style>
            body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #333; margin: 20px; }
            h1 { font-size: 20px; color: #46371c; margin-bottom: 5px; }
            p { font-size: 12px; color: #666; margin-top: 0; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 11px; }
            th { background-color: #f4f1ea; border: 1px solid #e5dfd3; color: #46371c; padding: 8px; text-align: left; font-weight: bold; }
            td { border: 1px solid #e5dfd3; padding: 6px 8px; }
            tr:nth-child(even) { background-color: #fcfbf9; }
            @media print {
              body { margin: 10px; }
              button { display: none; }
            }
          </style>
        </head>
        <body>
          <h1>${reportTitle}</h1>
          <p>Sede: ${storeName} | Rango: ${startDate} al ${endDate}</p>
          <table>
            <thead>
              <tr>
                ${tableHeaders.map(h => `<th>${h}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${tableRows.map(row => `
                <tr>
                  ${row.map(cell => `<td>${cell !== undefined && cell !== null ? cell : ''}</td>`).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
          ${summaryHtml}
          <script>
            window.onload = function() {
              window.print();
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [salesRes, topRes, statusRes, catRes] = await Promise.all([
          axios.get(`${API}/admin/reports/sales`, { withCredentials: true, headers: getHeaders(), params: { period } }),
          axios.get(`${API}/admin/reports/top-products`, { withCredentials: true, headers: getHeaders() }),
          axios.get(`${API}/admin/reports/status-distribution`, { withCredentials: true, headers: getHeaders() }),
          axios.get(`${API}/admin/reports/category-sales`, { withCredentials: true, headers: getHeaders() }),
        ]);
        setSalesData(salesRes.data.reverse());
        setTopProducts(topRes.data);
        setStatusDist(statusRes.data.map(d => ({ ...d, name: statusLabels[d.status] || d.status })));
        setCategorySales(catRes.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, [period]);

  const totalRevenue = salesData.reduce((s, d) => s + d.total, 0);
  const totalOrders = salesData.reduce((s, d) => s + d.orders, 0);

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#a0d5ba] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="admin-reports-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl font-bold text-[#46371c]">Reportes</h1>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-36 rounded-xl border-[#e5dfd3]" data-testid="period-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="daily">Diario</SelectItem>
            <SelectItem value="monthly">Mensual</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <Card className="border border-[#e5dfd3] bg-[#fcfbf9]">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-[#a0d5ba]/20 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-[#46371c]" /></div>
            <div><p className="font-body text-xs text-[#75664d]">Ventas Totales</p><p className="font-heading text-2xl font-bold text-[#46371c]">{formatPrice(totalRevenue)}</p></div>
          </CardContent>
        </Card>
        <Card className="border border-[#e5dfd3] bg-[#fcfbf9]">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center"><Package className="w-5 h-5 text-blue-700" /></div>
            <div><p className="font-body text-xs text-[#75664d]">Pedidos</p><p className="font-heading text-2xl font-bold text-[#46371c]">{totalOrders}</p></div>
          </CardContent>
        </Card>
        <Card className="border border-[#e5dfd3] bg-[#fcfbf9]">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center"><BarChart3 className="w-5 h-5 text-purple-700" /></div>
            <div><p className="font-body text-xs text-[#75664d]">Ticket Promedio</p><p className="font-heading text-2xl font-bold text-[#46371c]">{totalOrders > 0 ? formatPrice(totalRevenue / totalOrders) : '$0'}</p></div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-[#f4f1ea]">
          <TabsTrigger value="sales" className="font-body text-sm" data-testid="tab-sales">Ventas</TabsTrigger>
          <TabsTrigger value="products" className="font-body text-sm" data-testid="tab-products">Productos</TabsTrigger>
          <TabsTrigger value="status" className="font-body text-sm" data-testid="tab-status">Estado</TabsTrigger>
          <TabsTrigger value="categories" className="font-body text-sm" data-testid="tab-categories">Categorias</TabsTrigger>
          <TabsTrigger value="advanced" className="font-body text-sm" data-testid="tab-advanced">Reportes Avanzados</TabsTrigger>
        </TabsList>

        {/* Sales Chart */}
        <TabsContent value="sales">
          <Card className="border border-[#e5dfd3] bg-[#fcfbf9] p-6" data-testid="sales-chart">
            <h3 className="font-heading text-xl font-semibold text-[#46371c] mb-4">Ventas por {period === 'daily' ? 'dia' : 'mes'}</h3>
            {salesData.length === 0 ? (
              <p className="text-center py-12 font-body text-sm text-[#75664d]">No hay datos de ventas</p>
            ) : (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={salesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5dfd3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#75664d' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#75664d' }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => formatPrice(v)} contentStyle={{ borderRadius: '12px', border: '1px solid #e5dfd3' }} />
                  <Bar dataKey="total" fill="#a0d5ba" radius={[8, 8, 0, 0]} name="Ventas" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </TabsContent>

        {/* Top Products */}
        <TabsContent value="products">
          <Card className="border border-[#e5dfd3] bg-[#fcfbf9] p-6" data-testid="top-products-chart">
            <h3 className="font-heading text-xl font-semibold text-[#46371c] mb-4">Productos Mas Vendidos</h3>
            {topProducts.length === 0 ? (
              <p className="text-center py-12 font-body text-sm text-[#75664d]">No hay datos aun</p>
            ) : (
              <div className="space-y-3">
                {topProducts.map((p, i) => (
                  <div key={i} className="flex items-center gap-4 p-3 rounded-xl bg-[#f4f1ea]" data-testid={`top-product-${i}`}>
                    <span className="font-heading text-lg font-bold text-[#a0d5ba] w-8">#{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-body text-sm font-medium text-[#46371c] truncate">{p.name}</p>
                      <p className="font-body text-xs text-[#75664d]">{p.total_sold} vendidos</p>
                    </div>
                    <span className="font-body text-sm font-semibold text-[#46371c]">{formatPrice(p.revenue)}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Status Distribution */}
        <TabsContent value="status">
          <Card className="border border-[#e5dfd3] bg-[#fcfbf9] p-6" data-testid="status-chart">
            <h3 className="font-heading text-xl font-semibold text-[#46371c] mb-4">Distribucion por Estado</h3>
            {statusDist.length === 0 ? (
              <p className="text-center py-12 font-body text-sm text-[#75664d]">No hay datos</p>
            ) : (
              <div className="flex flex-col md:flex-row items-center gap-8">
                <ResponsiveContainer width="100%" height={300}>
                  <RPieChart>
                    <Pie data={statusDist} cx="50%" cy="50%" outerRadius={100} fill="#a0d5ba" dataKey="count" nameKey="name" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {statusDist.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </RPieChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Category Sales */}
        <TabsContent value="categories">
          <Card className="border border-[#e5dfd3] bg-[#fcfbf9] p-6" data-testid="categories-chart">
            <h3 className="font-heading text-xl font-semibold text-[#46371c] mb-4">Ventas por Categoria</h3>
            {categorySales.length === 0 ? (
              <p className="text-center py-12 font-body text-sm text-[#75664d]">No hay datos</p>
            ) : (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={categorySales} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5dfd3" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#75664d' }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="category" tick={{ fontSize: 11, fill: '#46371c' }} width={130} />
                  <Tooltip formatter={(v) => formatPrice(v)} contentStyle={{ borderRadius: '12px', border: '1px solid #e5dfd3' }} />
                  <Bar dataKey="revenue" fill="#46371c" radius={[0, 8, 8, 0]} name="Ingresos" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </TabsContent>

        {/* Advanced Reporting Tab */}
        <TabsContent value="advanced">
          <Card className="border border-[#e5dfd3] bg-[#fcfbf9] p-6">
            <h3 className="font-heading text-xl font-semibold text-[#46371c] mb-4 flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-[#46371c]" /> Generación de Reportes Administrativos
            </h3>

            {/* Filter controls */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 bg-[#f4f1ea]/40 p-4 rounded-2xl border border-[#e5dfd3]">
              <div>
                <label className="font-body text-xs text-[#75664d] mb-1.5 block font-semibold">Tipo de Reporte</label>
                <Select value={reportType} onValueChange={setReportType}>
                  <SelectTrigger className="rounded-xl border-[#e5dfd3] bg-white h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sales_by_ref">Ventas por Referencia</SelectItem>
                    <SelectItem value="production">Reporte de Producción</SelectItem>
                    <SelectItem value="pending_deliveries">Reporte de Entregas Pendientes</SelectItem>
                    <SelectItem value="payments">Reporte de Pagos</SelectItem>
                    <SelectItem value="delivered_orders">📦 Pedidos Entregados (Tienda y Domicilio)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="font-body text-xs text-[#75664d] mb-1.5 block font-semibold">Sede / Tienda</label>
                <Select value={selectedStoreId} onValueChange={setSelectedStoreId}>
                  <SelectTrigger className="rounded-xl border-[#e5dfd3] bg-white h-10">
                    <SelectValue placeholder="Todas las tiendas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las sedes</SelectItem>
                    {stores.map(store => (
                      <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="font-body text-xs text-[#75664d] mb-1.5 block font-semibold">Fecha Inicio</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] bg-white font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                />
              </div>

              <div>
                <label className="font-body text-xs text-[#75664d] mb-1.5 block font-semibold">Fecha Fin</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] bg-white font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                />
              </div>

              {reportType === 'delivered_orders' && (
                <div>
                  <label className="font-body text-xs text-[#75664d] mb-1.5 block font-semibold">Tipo de Entrega</label>
                  <Select value={deliveryTypeFilter} onValueChange={setDeliveryTypeFilter}>
                    <SelectTrigger className="w-full h-10 rounded-xl border-[#e5dfd3] bg-white font-body text-sm text-[#46371c]">
                      <SelectValue placeholder="Todos los tipos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos (Domicilios y Tienda)</SelectItem>
                      <SelectItem value="delivery">🛵 Solo Domicilios</SelectItem>
                      <SelectItem value="pickup">🏬 Solo Retiro en Tienda</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {reportType === 'production' && (
                <div>
                  <label className="font-body text-xs text-[#75664d] mb-1.5 block font-semibold">Filtrar por Categoría</label>
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger className="w-full h-10 rounded-xl border-[#e5dfd3] bg-white font-body text-sm text-[#46371c]">
                      <SelectValue placeholder="Todas las categorías" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las categorías</SelectItem>
                      <SelectItem value="Refrigeradas">Refrigeradas</SelectItem>
                      <SelectItem value="Refrigeradas Premium">Refrigeradas Premium</SelectItem>
                      <SelectItem value="Tortas Especiales">Tortas Especiales</SelectItem>
                      <SelectItem value="Tortas Personalizadas">Tortas Personalizadas</SelectItem>
                      <SelectItem value="Tortas Temáticas">Tortas Temáticas</SelectItem>
                      <SelectItem value="Clasicas">Clásicas</SelectItem>
                      <SelectItem value="Saludables">Saludables</SelectItem>
                      <SelectItem value="Mesa de Dulces">Mesa de Dulces</SelectItem>
                      <SelectItem value="Detalles y Regalos">Detalles y Regalos</SelectItem>
                      <SelectItem value="Piñatería">Piñatería</SelectItem>
                      <SelectItem value="Grados">Grados</SelectItem>
                      <SelectItem value="Primera Comunión">Primera Comunión</SelectItem>
                      <SelectItem value="Tortas">Tortas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 mb-6">
              <Button
                onClick={generateReport}
                disabled={generatingReport}
                className="bg-[#46371c] hover:bg-[#2d2312] text-[#fcfbf9] rounded-xl font-body flex-1 h-10"
              >
                {generatingReport ? 'Generando...' : 'Generar Reporte'}
              </Button>
              <Button
                onClick={exportCSV}
                disabled={reportData.length === 0}
                className="bg-green-600 hover:bg-green-700 text-white rounded-xl font-body flex items-center justify-center gap-2 h-10 px-6 disabled:opacity-50"
              >
                <Download className="w-4 h-4" /> Exportar CSV
              </Button>
              <Button
                onClick={exportPDF}
                disabled={reportData.length === 0}
                className="bg-red-600 hover:bg-red-700 text-white rounded-xl font-body flex items-center justify-center gap-2 h-10 px-6 disabled:opacity-50"
              >
                <FileText className="w-4 h-4" /> Exportar PDF
              </Button>
            </div>

            {/* KPI Cards for Delivered Orders */}
            {reportType === 'delivered_orders' && reportData.length > 0 && (() => {
              let totalVentas = 0;
              let totalPagado = 0;
              let totalPendiente = 0;
              let totalFletes = 0;
              let countDelivery = 0;
              let countPickup = 0;
              let cakesSmall = 0;
              let cakesMedium = 0;
              let cakesLarge = 0;
              let cakesOther = 0;
              let totalItems = 0;

              reportData.forEach(row => {
                totalVentas += (Number(row.total_orden) || 0);
                totalPagado += (Number(row.valor_pagado) || 0);
                totalPendiente += (Number(row.saldo_pendiente) || 0);
                totalFletes += (Number(row.valor_domicilio) || 0);
                if (row.tipo_entrega === 'delivery') countDelivery++;
                else countPickup++;

                if (row.cake_counts) {
                  cakesSmall += (row.cake_counts.small || 0);
                  cakesMedium += (row.cake_counts.medium || 0);
                  cakesLarge += (row.cake_counts.large || 0);
                  cakesOther += (row.cake_counts.other || 0);
                }
                totalItems += (Number(row.total_items_count) || 1);
              });

              return (
                <div className="space-y-4 mb-6">
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <Card className="border border-[#e5dfd3] bg-white p-3.5 rounded-xl shadow-sm">
                      <p className="font-body text-[11px] text-[#75664d] flex items-center justify-between">
                        <span>Total Entregados</span>
                        <Package className="w-3.5 h-3.5 text-blue-600" />
                      </p>
                      <p className="font-heading text-xl font-bold text-[#46371c] mt-1">{reportData.length}</p>
                      <p className="text-[10px] text-[#75664d] mt-0.5 truncate">
                        🛵 {countDelivery} Domicilio • 🏬 {countPickup} Tienda
                      </p>
                    </Card>

                    <Card className="border border-[#e5dfd3] bg-white p-3.5 rounded-xl shadow-sm">
                      <p className="font-body text-[11px] text-[#75664d] flex items-center justify-between">
                        <span>Total Facturado</span>
                        <DollarSign className="w-3.5 h-3.5 text-[#46371c]" />
                      </p>
                      <p className="font-heading text-xl font-bold text-[#46371c] mt-1">{formatPrice(totalVentas)}</p>
                      <p className="text-[10px] text-[#75664d] mt-0.5">
                        Fletes: {formatPrice(totalFletes)}
                      </p>
                    </Card>

                    <Card className="border border-green-200 bg-green-50/30 p-3.5 rounded-xl shadow-sm">
                      <p className="font-body text-[11px] text-green-800 flex items-center justify-between font-medium">
                        <span>Total Recaudado</span>
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                      </p>
                      <p className="font-heading text-xl font-bold text-green-800 mt-1">{formatPrice(totalPagado)}</p>
                      <p className="text-[10px] text-green-700 mt-0.5">
                        Pagos confirmados
                      </p>
                    </Card>

                    <Card className={`border p-3.5 rounded-xl shadow-sm ${totalPendiente > 0 ? 'border-amber-300 bg-amber-50/50' : 'border-green-200 bg-white'}`}>
                      <p className={`font-body text-[11px] flex items-center justify-between font-medium ${totalPendiente > 0 ? 'text-amber-900 font-bold' : 'text-[#75664d]'}`}>
                        <span>Saldo Pendiente</span>
                        <AlertCircle className={`w-3.5 h-3.5 ${totalPendiente > 0 ? 'text-amber-600' : 'text-green-600'}`} />
                      </p>
                      <p className={`font-heading text-xl font-bold mt-1 ${totalPendiente > 0 ? 'text-amber-700' : 'text-green-700'}`}>
                        {formatPrice(totalPendiente)}
                      </p>
                      <p className="text-[10px] text-amber-800 mt-0.5">
                        {totalPendiente > 0 ? '⚠️ Dinero por cobrar/cuadrar' : '✅ 100% al día'}
                      </p>
                    </Card>

                    <Card className="border border-purple-200 bg-purple-50/30 p-3.5 rounded-xl shadow-sm col-span-2 sm:col-span-1">
                      <p className="font-body text-[11px] text-purple-900 flex items-center justify-between font-medium">
                        <span>Inventario Tortas</span>
                        <Layers className="w-3.5 h-3.5 text-purple-600" />
                      </p>
                      <p className="font-heading text-xl font-bold text-purple-950 mt-1">{totalItems} unds</p>
                      <p className="text-[10px] text-purple-800 mt-0.5 truncate">
                        P: {cakesSmall} • M: {cakesMedium} • G: {cakesLarge}
                      </p>
                    </Card>
                  </div>
                </div>
              );
            })()}

            {/* Preview Table */}
            {reportData.length > 0 ? (
              <div className="overflow-x-auto border border-[#e5dfd3] rounded-xl bg-white">
                <table className="w-full text-left border-collapse text-xs font-body">
                  <thead>
                    <tr className="bg-[#f4f1ea]/80 border-b border-[#e5dfd3] text-[#46371c] font-semibold">
                      {reportType === 'sales_by_ref' && (
                        <>
                          <th className="p-3">Código</th>
                          <th className="p-3">Referencia</th>
                          <th className="p-3">Tamaño</th>
                          <th className="p-3">Categoría</th>
                          <th className="p-3 text-center">Cant.</th>
                          <th className="p-3 text-right">Valor Total</th>
                          <th className="p-3">Órdenes</th>
                        </>
                      )}
                      {reportType === 'production' && (
                        <>
                          <th className="p-3">Fecha</th>
                          <th className="p-3">Orden</th>
                          <th className="p-3">Producto</th>
                          <th className="p-3 text-center">Cant.</th>
                          <th className="p-3">Sabor</th>
                          <th className="p-3">Relleno</th>
                          <th className="p-3">Tamaño</th>
                          <th className="p-3">Categoría</th>
                          <th className="p-3">Producir</th>
                          <th className="p-3">Entrega</th>
                          <th className="p-3">Horario</th>
                          <th className="p-3">Estado</th>
                          <th className="p-3">Sede</th>
                        </>
                      )}
                      {reportType === 'pending_deliveries' && (
                        <>
                          <th className="p-3">Fecha</th>
                          <th className="p-3">Tienda</th>
                          <th className="p-3">Categoría</th>
                          <th className="p-3 text-center">Cantidad</th>
                          <th className="p-3 text-right">Valor</th>
                          <th className="p-3 text-center">Num Órdenes</th>
                        </>
                      )}
                      {reportType === 'payments' && (
                        <>
                          <th className="p-3">Fecha Pago</th>
                          <th className="p-3">Orden</th>
                          <th className="p-3">Cliente</th>
                          <th className="p-3">Usuario</th>
                          <th className="p-3">Método</th>
                          <th className="p-3 text-right">Valor Pago</th>
                          <th className="p-3 text-right">Total Orden</th>
                          <th className="p-3 text-right">Saldo Pendiente</th>
                          <th className="p-3">Sede</th>
                        </>
                      )}
                      {reportType === 'delivered_orders' && (
                        <>
                          <th className="p-3">Orden</th>
                          <th className="p-3">Tipo</th>
                          <th className="p-3">Sede</th>
                          <th className="p-3">Domiciliario</th>
                          <th className="p-3">Cliente</th>
                          <th className="p-3">Dirección / Punto</th>
                          <th className="p-3">Horario</th>
                          <th className="p-3">Productos e Inventario</th>
                          <th className="p-3 text-right">Total</th>
                          <th className="p-3 text-right">Pagado</th>
                          <th className="p-3 text-right">Saldo Pend.</th>
                          <th className="p-3">Método</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {reportData
                      .filter(row => {
                        if (reportType === 'production' && selectedCategory !== 'all') {
                          const cat = (row.categoria || '').toLowerCase();
                          const sel = selectedCategory.toLowerCase();
                          if (sel.includes('personalizada') || sel.includes('tortas personalizadas')) {
                            return cat.includes('personalizada');
                          }
                          return cat === sel;
                        }
                        return true;
                      })
                      .map((row, index) => (
                      <tr key={index} className="border-b border-[#e5dfd3]/60 hover:bg-[#f4f1ea]/20 text-[#75664d]">
                        {reportType === 'sales_by_ref' && (
                          <>
                            <td className="p-3 font-semibold text-[#46371c]">#{row.codigo}</td>
                            <td className="p-3 font-medium text-[#46371c]">{row.referencia}</td>
                            <td className="p-3">{row.tamano}</td>
                            <td className="p-3"><Badge className="bg-[#f4f1ea] text-[#46371c] border-none text-[10px]">{row.categoria}</Badge></td>
                            <td className="p-3 text-center font-bold text-[#46371c]">{row.num}</td>
                            <td className="p-3 text-right font-semibold text-[#46371c]">{formatPrice(row.valor)}</td>
                            <td className="p-3 truncate max-w-[200px]">{row.ordenes}</td>
                          </>
                        )}
                        {reportType === 'production' && (
                          <>
                            <td className="p-3">{row.fecha}</td>
                            <td className="p-3 font-semibold text-[#46371c]">#{row.orden}</td>
                            <td className="p-3 font-medium text-[#46371c]">{row.producto}</td>
                            <td className="p-3 text-center font-bold">{row.cantidad}</td>
                            <td className="p-3">{row.sabor}</td>
                            <td className="p-3">{row.relleno}</td>
                            <td className="p-3">{row.tamano}</td>
                            <td className="p-3">{row.categoria}</td>
                            <td className="p-3">{row.producir_fecha}</td>
                            <td className="p-3">{row.entrega_fecha}</td>
                            <td className="p-3">{row.entrega_hora}</td>
                            <td className="p-3"><Badge className="bg-purple-100 text-purple-800 border-none text-[10px]">{row.estado}</Badge></td>
                            <td className="p-3">{row.sede}</td>
                          </>
                        )}
                        {reportType === 'pending_deliveries' && (
                          <>
                            <td className="p-3 font-medium text-[#46371c]">{row.fecha}</td>
                            <td className="p-3">{row.tienda}</td>
                            <td className="p-3"><Badge className="bg-[#f4f1ea] text-[#46371c] border-none text-[10px]">{row.categoria}</Badge></td>
                            <td className="p-3 text-center font-bold">{row.cantidad}</td>
                            <td className="p-3 text-right font-semibold text-[#46371c]">{formatPrice(row.valor)}</td>
                            <td className="p-3 text-center">{row.num_ordenes}</td>
                          </>
                        )}
                        {reportType === 'payments' && (
                          <>
                            <td className="p-3 font-medium text-[#46371c]">{row.fecha_pago}</td>
                            <td className="p-3 font-semibold text-[#46371c]">#{row.orden}</td>
                            <td className="p-3">{row.cliente}</td>
                            <td className="p-3 font-medium text-[#46371c]">{row.usuario_pago || 'N/A'}</td>
                            <td className="p-3"><Badge className="bg-blue-100 text-blue-800 border-none text-[10px]">{row.metodo}</Badge></td>
                            <td className="p-3 text-right font-semibold text-green-700">{formatPrice(row.valor_pago)}</td>
                            <td className="p-3 text-right font-semibold text-[#46371c]">{formatPrice(row.total_orden)}</td>
                            <td className={`p-3 text-right font-semibold ${(row.saldo_pendiente || 0) > 0 ? 'text-amber-700 font-bold' : 'text-green-700'}`}>
                              {formatPrice(row.saldo_pendiente || 0)}
                            </td>
                            <td className="p-3">{row.sede}</td>
                          </>
                        )}
                        {reportType === 'delivered_orders' && (
                          <>
                            <td className="p-3 font-semibold text-[#46371c]">#{row.orden}</td>
                            <td className="p-3">
                              {row.tipo_entrega === 'delivery' ? (
                                <Badge className="bg-orange-100 text-orange-800 border-orange-200 text-[10px] flex items-center gap-1 w-fit">
                                  <Truck className="w-3 h-3" /> Domicilio
                                </Badge>
                              ) : (
                                <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-[10px] flex items-center gap-1 w-fit">
                                  <Store className="w-3 h-3" /> Tienda
                                </Badge>
                              )}
                            </td>
                            <td className="p-3 font-medium text-[#46371c]">{row.sede}</td>
                            <td className="p-3">
                              <span className="font-semibold text-purple-900 bg-purple-50 px-2 py-0.5 rounded text-[11px] inline-block">
                                {row.domiciliario}
                              </span>
                            </td>
                            <td className="p-3">
                              <p className="font-medium text-[#46371c]">{row.cliente}</p>
                              <p className="text-[10px] text-[#75664d]">{row.telefono}</p>
                            </td>
                            <td className="p-3 max-w-[160px] truncate" title={row.direccion}>
                              {row.direccion}
                            </td>
                            <td className="p-3 text-[11px] whitespace-nowrap">{row.hora_entrega || 'N/A'}</td>
                            <td className="p-3 max-w-[240px]">
                              <p className="text-[11px] text-[#46371c] font-medium line-clamp-2" title={row.productos_resumen}>
                                {row.productos_resumen}
                              </p>
                              <span className="text-[10px] text-amber-800 font-semibold bg-amber-50 px-1.5 py-0.5 rounded mt-0.5 inline-block">
                                {row.total_items_count || 1} und(s)
                              </span>
                            </td>
                            <td className="p-3 text-right font-semibold text-[#46371c]">{formatPrice(row.total_orden)}</td>
                            <td className="p-3 text-right font-semibold text-green-700">{formatPrice(row.valor_pagado)}</td>
                            <td className={`p-3 text-right font-semibold ${(row.saldo_pendiente || 0) > 0 ? 'text-amber-700 font-bold bg-amber-50/60' : 'text-green-700'}`}>
                              {formatPrice(row.saldo_pendiente || 0)}
                            </td>
                            <td className="p-3">
                              <Badge className="bg-[#f4f1ea] text-[#46371c] border-none text-[10px] uppercase">{row.metodo_pago}</Badge>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {reportType === 'production' && (() => {
                  const filteredProd = reportData.filter(row => {
                    if (selectedCategory !== 'all') {
                      const cat = (row.categoria || '').toLowerCase();
                      const sel = selectedCategory.toLowerCase();
                      if (sel.includes('personalizada') || sel.includes('tortas personalizadas')) {
                        return cat.includes('personalizada');
                      }
                      return cat === sel;
                    }
                    return true;
                  });
                  const categoryTotals = {};
                  let totalItems = 0;
                  filteredProd.forEach(row => {
                    const cat = row.categoria || 'Sin Categoría';
                    const qty = Number(row.cantidad) || 1;
                    categoryTotals[cat] = (categoryTotals[cat] || 0) + qty;
                    totalItems += qty;
                  });

                  return (
                    <div className="p-4 bg-[#fcfbf9] border-t border-[#e5dfd3] flex justify-end">
                      <div className="w-full max-w-md bg-white p-4 rounded-xl border border-[#e5dfd3] space-y-2.5 shadow-sm">
                        <h4 className="font-heading text-xs font-bold text-[#46371c] border-b border-[#e5dfd3]/60 pb-1.5 uppercase tracking-wide">
                          📊 Consolidado de Producción por Categoría
                        </h4>
                        <div className="space-y-1.5 text-xs font-body text-[#75664d]">
                          {Object.entries(categoryTotals).map(([cat, total]) => (
                            <div key={cat} className="flex justify-between items-center py-0.5 border-b border-gray-50">
                              <span className="font-medium text-[#46371c]">{cat}:</span>
                              <span className="font-semibold text-purple-900 bg-purple-50 px-2 py-0.5 rounded-md">{total} {total === 1 ? 'unidad' : 'unidades'}</span>
                            </div>
                          ))}
                          <div className="flex justify-between border-t-2 border-[#46371c]/20 pt-2.5 mt-2 font-bold text-sm text-[#46371c]">
                            <span>TOTAL UNIDADES A PRODUCIR:</span>
                            <span className="text-[#46371c] bg-[#a0d5ba]/30 px-2.5 py-1 rounded-lg">
                              {totalItems} unidades
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {reportType === 'payments' && (() => {
                  const methodTotals = {};
                  let totalSum = 0;
                  reportData.forEach(row => {
                    const method = row.metodo || 'SIN PAGO';
                    const amount = Number(row.valor_pago) || 0;
                    methodTotals[method] = (methodTotals[method] || 0) + amount;
                    totalSum += amount;
                  });

                  return (
                    <div className="p-4 bg-[#fcfbf9] border-t border-[#e5dfd3] flex justify-end">
                      <div className="w-full max-w-sm bg-white p-4 rounded-xl border border-[#e5dfd3] space-y-2.5 shadow-sm">
                        <h4 className="font-heading text-xs font-bold text-[#46371c] border-b border-[#e5dfd3]/60 pb-1.5 uppercase">Consolidado por Forma de Pago</h4>
                        <div className="space-y-1.5 text-xs font-body text-[#75664d]">
                          {Object.entries(methodTotals).map(([method, total]) => (
                            <div key={method} className="flex justify-between">
                              <span className="uppercase font-medium">{method}:</span>
                              <span className="font-semibold text-[#46371c]">{formatPrice(total)}</span>
                            </div>
                          ))}
                          <div className="flex justify-between border-t border-[#e5dfd3]/60 pt-2 mt-2 font-bold text-sm text-[#46371c]">
                            <span>TOTAL RECAUDADO:</span>
                            <span className="text-green-700">{formatPrice(totalSum)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {reportType === 'delivered_orders' && (() => {
                  let totalVentas = 0;
                  let totalPagado = 0;
                  let totalPendiente = 0;
                  let totalFletes = 0;
                  let countDelivery = 0;
                  let countPickup = 0;
                  let cakesSmall = 0;
                  let cakesMedium = 0;
                  let cakesLarge = 0;
                  let cakesOther = 0;
                  let totalItems = 0;

                  reportData.forEach(row => {
                    totalVentas += (Number(row.total_orden) || 0);
                    totalPagado += (Number(row.valor_pagado) || 0);
                    totalPendiente += (Number(row.saldo_pendiente) || 0);
                    totalFletes += (Number(row.valor_domicilio) || 0);
                    if (row.tipo_entrega === 'delivery') countDelivery++;
                    else countPickup++;

                    if (row.cake_counts) {
                      cakesSmall += (row.cake_counts.small || 0);
                      cakesMedium += (row.cake_counts.medium || 0);
                      cakesLarge += (row.cake_counts.large || 0);
                      cakesOther += (row.cake_counts.other || 0);
                    }
                    totalItems += (Number(row.total_items_count) || 1);
                  });

                  return (
                    <div className="p-5 bg-[#fcfbf9] border-t border-[#e5dfd3] grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Cuadre de Plata */}
                      <div className="bg-white p-4 rounded-xl border border-[#e5dfd3] space-y-2 shadow-sm">
                        <h4 className="font-heading text-xs font-bold text-[#46371c] border-b border-[#e5dfd3]/60 pb-1.5 uppercase flex items-center gap-1.5">
                          <DollarSign className="w-3.5 h-3.5 text-green-700" /> Cuadre de Dinero / Recaudo
                        </h4>
                        <div className="space-y-1.5 text-xs font-body text-[#75664d]">
                          <div className="flex justify-between py-0.5">
                            <span>Pedidos a Domicilio:</span>
                            <span className="font-semibold text-[#46371c]">{countDelivery} pedidos</span>
                          </div>
                          <div className="flex justify-between py-0.5">
                            <span>Pedidos en Tienda:</span>
                            <span className="font-semibold text-[#46371c]">{countPickup} pedidos</span>
                          </div>
                          <div className="flex justify-between py-0.5">
                            <span>Fletes de Domicilio:</span>
                            <span className="font-semibold text-[#46371c]">{formatPrice(totalFletes)}</span>
                          </div>
                          <div className="flex justify-between py-0.5">
                            <span>Total Facturado:</span>
                            <span className="font-semibold text-[#46371c]">{formatPrice(totalVentas)}</span>
                          </div>
                          <div className="flex justify-between py-0.5 border-t border-gray-100 pt-1 text-green-700 font-semibold">
                            <span>Total Recaudado / Pagado:</span>
                            <span>{formatPrice(totalPagado)}</span>
                          </div>
                          <div className="flex justify-between border-t-2 border-[#46371c]/20 pt-2 font-bold text-sm">
                            <span className={totalPendiente > 0 ? 'text-amber-800' : 'text-[#46371c]'}>SALDO PENDIENTE POR COBRAR:</span>
                            <span className={totalPendiente > 0 ? 'text-amber-700 bg-amber-100 px-2 py-0.5 rounded-lg' : 'text-green-700'}>
                              {formatPrice(totalPendiente)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Cuadre de Inventario */}
                      <div className="bg-white p-4 rounded-xl border border-[#e5dfd3] space-y-2 shadow-sm">
                        <h4 className="font-heading text-xs font-bold text-[#46371c] border-b border-[#e5dfd3]/60 pb-1.5 uppercase flex items-center gap-1.5">
                          <Layers className="w-3.5 h-3.5 text-purple-700" /> Control de Inventario Entregado
                        </h4>
                        <div className="space-y-1.5 text-xs font-body text-[#75664d]">
                          <div className="flex justify-between py-0.5">
                            <span>Tortas Pequeñas (Personal / 6-8):</span>
                            <span className="font-semibold text-purple-900 bg-purple-50 px-2 py-0.5 rounded-md">{cakesSmall} unds</span>
                          </div>
                          <div className="flex justify-between py-0.5">
                            <span>Tortas Medianas (12-15):</span>
                            <span className="font-semibold text-purple-900 bg-purple-50 px-2 py-0.5 rounded-md">{cakesMedium} unds</span>
                          </div>
                          <div className="flex justify-between py-0.5">
                            <span>Tortas Grandes (20-35):</span>
                            <span className="font-semibold text-purple-900 bg-purple-50 px-2 py-0.5 rounded-md">{cakesLarge} unds</span>
                          </div>
                          <div className="flex justify-between py-0.5">
                            <span>Otros Productos / Complementos:</span>
                            <span className="font-semibold text-purple-900 bg-purple-50 px-2 py-0.5 rounded-md">{cakesOther} unds</span>
                          </div>
                          <div className="flex justify-between border-t-2 border-[#46371c]/20 pt-2 font-bold text-sm text-[#46371c]">
                            <span>TOTAL UNIDADES ENTREGADAS:</span>
                            <span className="bg-[#a0d5ba]/30 px-2.5 py-1 rounded-lg">
                              {totalItems} unidades
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div className="text-center py-12 border border-dashed border-[#e5dfd3] bg-white rounded-xl">
                <FileSpreadsheet className="w-10 h-10 text-[#e5dfd3] mx-auto mb-2" />
                <p className="font-body text-xs text-[#75664d]">No se han generado datos de reporte. Selecciona los filtros y haz clic en "Generar Reporte".</p>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
